import React, { useState, useCallback, useEffect } from 'react'
import { View, TextInput, StyleSheet } from 'react-native'
import { DS } from '../../design-system'
import MaterialIcons from '@expo/vector-icons/MaterialIcons'

interface SearchBarProps {
  onSearch: (searchTerm: string) => void
  placeholder?: string
  debounceMs?: number
}

export const SearchBar = ({
  onSearch,
  placeholder = 'Search users by name or email...',
  debounceMs = 500
}: SearchBarProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, debounceMs])

  useEffect(() => {
    const searchTerm = debouncedTerm.trim()
    onSearch(searchTerm.length >= 2 ? searchTerm : '')
  }, [debouncedTerm, onSearch])

  const handleClear = useCallback(() => {
    setSearchTerm('')
    onSearch('')
  }, [onSearch])

  return (
    <View style={styles.container}>
      <View style={styles.searchContainer}>
        <MaterialIcons name="search" size={20} color={DS.palette['Brand/Dark Grey']} style={styles.searchIcon} />
        <TextInput
          style={styles.input}
          placeholder={placeholder}
          placeholderTextColor={DS.palette['Brand/Dark Grey']}
          value={searchTerm}
          onChangeText={setSearchTerm}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchTerm.length > 0 && (
          <MaterialIcons
            name="close"
            size={20}
            color={DS.palette['Brand/Dark Grey']}
            style={styles.clearIcon}
            onPress={handleClear}
          />
        )}
      </View>
      {searchTerm.trim().length > 0 && searchTerm.trim().length < 2 && (
        <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey" style={styles.helperText}>
          Type at least 2 characters to search
        </DS.Text>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    gap: 8
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8
  },
  searchIcon: {
    marginRight: 4
  },
  input: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter',
    color: DS.palette['Brand/Dark Grey']
  },
  clearIcon: {
    marginLeft: 4,
    cursor: 'pointer'
  },
  helperText: {
    marginLeft: 4
  }
})
